import { Action, AnyAction, Reducer } from 'redux';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from './../../';
import * as BrowserStateModels from './../../Models/SharedMaterials/BrowserStateModels';
import * as Models from './../../Models/SharedMaterials/DataModel';
import { ToxnotServiceClient } from './../../ToxnotServiceClient';
import '../../../utilities/Utilities';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { AppServices } from '../../../AppServices';
import { SharedMaterialFilterCheckContext, SharedMaterialsFilteringManager } from './SharedMaterialFiltering';
import { Filter, FilterId } from '../../Models/Filter';
import { SurveyEditorCreateNewSurveyRequestData } from '../../Models/Api/SurveyEditorCreateNewSurveyRequestModel';
import * as SurveyStateController from '../../../store/StateControllers/Survey/SurveyStateController';
import { SharedMaterialCoreData } from './../../Models/SharedMaterials/DataModel';
import { SurveyState } from '../../Models/Surveys/SurveyDataModel';
import { ResultType } from '../../Models/Api/ApiResponse';
import { DynamicRegulation } from "../../Models/Compliance/DynamicRegulation";
import { FilterMenu, SortDirectionType } from "./../../Models/SharedMaterials/BrowserStateModels";
import { SharedMaterialsCatalogRequest } from '../../Models/Api/SharedMaterials/SharedMaterialsCatalogResult';

export type SharedMaterialDataModelType = Models.SharedMaterialCoreData | Models.SharedMaterialsCatalogCompany;

export const rowPerPageValue: number = 50;

// ===============================================================================

//
// State definition
//

export enum AddToLibraryState {
    Default,
    Adding,
    Added,
    Failed
}

export enum SharedMaterialDetailStatus {
    Loading,
    NotFound,
    Found
}

export interface BrowserState {
    sortContext: BrowserStateModels.SortContext;
    activeFilters: Filter[];
    filteredSharedMaterialsCoreData: Models.SharedMaterialCoreData[];
    browserRowModels: SharedMaterialDataModelType[],
    filterMenus: BrowserStateModels.FilterMenu[];
    currentlySelectedSharedMaterialIds: { [id: string]: string };
    searchMode: BrowserStateModels.BrowserSearchMode;
}

interface SpecificBrowserStateData {
    filteredSharedMaterialsCoreData: Models.SharedMaterialCoreData[];
    filterMenus: BrowserStateModels.FilterMenu[];
    browserRowModels: SharedMaterialDataModelType[];
}

export interface SharedMaterialDetailState {
    status: SharedMaterialDetailStatus;
    detailBundle: Models.SharedMaterialDetailResponse | null;
    collapsedSections: string[];
    addingState: AddToLibraryState;
    isSlidingDrawerOpen: boolean;
    isSlidingDrawerDataLoaded: boolean;
    isSlidingDrawerDataError: boolean;
    isSendingSurveys: boolean;
    isSendingSurveysError: boolean;
    isSendingSurveysSuccess: boolean;
    isSnapshotSurveyStateSaved: boolean;
    snapshotSurveyState: SurveyState
}

export interface RequestState {
    state: BrowserStateModels.RequestState;
}

export interface CurrentlySelectedSupplierState {
    isLoading: boolean;
    supplier: Models.SharedMaterialsFullCompany | null;
}

export interface SharedMaterialsState {
    isLoadingCatalog: boolean;
    sharedMaterialsCoreData: SharedMaterialDataModelType[];
    sharedMaterialsCoreDataMap: { [sharedMaterialId: string]: Models.SharedMaterialCoreData };
    totalNumberOfCatalog: number;
    currentPage: number;
    homeBrowserState: BrowserState;
    supplierBrowserState: BrowserState;
    sharedMaterialDetailState: SharedMaterialDetailState;
    requestState: RequestState | null;
    currentlySelectedSupplierState: CurrentlySelectedSupplierState;
    hasSeenExchangeFirstRunExperience: boolean;
    dynamicRegulationTable: Readonly<{ [regulationId: string]: DynamicRegulation }>;
}

// ===============================================================================

//
// Action definitions
//

export enum BrowserEndpoint {
    Home,
    Supplier
}

const RequestSharedMaterialsCompaniesDataValue = "REQUEST_SHARED_MATERIALS_COMPANIES_DATA";
const ReceiveSharedMaterialsCompaniesDataValue = "RECEIVE_SHARED_MATERIALS_COMPANIES_DATA";

interface RequestSharedMaterialsCompaniesDataAction {
    type: typeof RequestSharedMaterialsCompaniesDataValue;
}

interface ReceiveSharedMaterialsCompaniesDataAction {
    type: typeof ReceiveSharedMaterialsCompaniesDataValue;
    sharedMaterialsCoreData: Models.SharedMaterialsCatalogCompany[];
    totalNumberOfCatalog: number;
    currentPage: number;
}

const RequestSharedMaterialsCoreDataValue = "REQUEST_SHARED_MATERIALS_CORE_DATA";
type RequestSharedMaterialsCoreDataType = typeof RequestSharedMaterialsCoreDataValue;

interface RequestSharedMaterialsCoreDataAction {
    type: RequestSharedMaterialsCoreDataType;
}

const ReceiveSharedMaterialsCoreDataValue = "RECEIVE_SHARED_MATERIALS_CORE_DATA";
type ReceiveSharedMaterialsCoreDataType = typeof ReceiveSharedMaterialsCoreDataValue;

interface ReceiveSharedMaterialsCoreDataAction {
    type: ReceiveSharedMaterialsCoreDataType;
    sharedMaterialsCoreData: SharedMaterialDataModelType[]
    sharedMaterialsCoreDataMap: { [sharedMaterialId: string]: Models.SharedMaterialCoreData };
    allSharedMaterialsSuppliers: { [supplierId: string]: Models.SharedMaterialsCatalogCompany };
    specificHomeBrowserStateData: SpecificBrowserStateData;
    specificSupplierBrowserStateData: SpecificBrowserStateData;
    dynamicRegulationTable: Readonly<{ [regulationId: string]: DynamicRegulation }>;
    totalNumberOfCatalog: number;
    currentPage: number;
    categories: string[];
}


const SetSortValue = "SET_SORT";
type SetSortType = typeof SetSortValue;

interface SetSortAction {
    type: SetSortType;
    browserEndpoint: BrowserEndpoint;
    sortContext: BrowserStateModels.SortContext;
    filteredSharedMaterialsCoreData: Models.SharedMaterialCoreData[];
    browserRows: SharedMaterialDataModelType[];
}

const FilterActionValue = "FILTER_ACTION";
type FilterActionType = typeof FilterActionValue;

interface FilterAction {
    type: FilterActionType;
    browserEndpoint: BrowserEndpoint;
    activeFilters: Filter[];
    filteredSharedMaterialsCoreData: Models.SharedMaterialCoreData[];
    browserRows: SharedMaterialDataModelType[];
}

const UpdateFilterMenusValue = "UPDATE_FILTER_MENUS";
type UpdateFilterMenusType = typeof UpdateFilterMenusValue;

interface UpdateFilterMenusAction {
    type: UpdateFilterMenusType;
    filterMenus: BrowserStateModels.FilterMenu[];
    browserEndpoint: BrowserEndpoint;
}

const UpdateDetailStateValue = "UPDATE_DETAIL_STATE";
type UpdateDetailStateType = typeof UpdateDetailStateValue;

interface UpdateDetailStateAction {
    type: UpdateDetailStateType
    value: Partial<SharedMaterialDetailState>;
}

const UpdateRequestStateValue = "UPDATE_REQUEST_STATE";
type UpdateRequestStateType = typeof UpdateRequestStateValue;

interface UpdateRequestStateAction {
    type: UpdateRequestStateType
    requestState: BrowserStateModels.RequestState | null;
}

const RequestSharedMaterialDetailValue = "REQUEST_SHARED_MATERIAL_DETAIL";
type RequestSharedMaterialDetailType = typeof RequestSharedMaterialDetailValue;

interface RequestSharedMaterialDetailAction {
    type: RequestSharedMaterialDetailType;
}

const ReceiveSharedMaterialDetailValue = "RECEIVE_SHARED_MATERIAL_DETAIL";
type ReceiveSharedMaterialDetailType = typeof ReceiveSharedMaterialDetailValue;

interface ReceiveSharedMaterialDetailAction {
    type: ReceiveSharedMaterialDetailType;
    detailBundle: Models.SharedMaterialDetailResponse;
}

const UpdateDetailDataSectionsValue = "UPDATE_DETAIL_DATA_SECTIONS";
type UpdateDetailDataSectionsType = typeof UpdateDetailDataSectionsValue;

interface UpdateDetailDataSectionsAction {
    type: UpdateDetailDataSectionsType;
    collapsedSections: string[];
}

const UpdateSelectedSharedMaterialsValue = "UPDATE_SELECTED_SHARED_MATERIALS";
type UpdateSelectedSharedMaterialsType = typeof UpdateSelectedSharedMaterialsValue;

interface UpdateSelectedSharedMaterialsAction {
    type: UpdateSelectedSharedMaterialsType;
    selectedMaterialIds: { [id: string]: string };
}

const RequestSupplierWithOwnerIdValue = "REQUEST_SUPPLIER_WITH_OWNER_ID";

interface RequestSupplierWithOwnerIdAction {
    type: typeof RequestSupplierWithOwnerIdValue;
}

const ReceiveSupplierWithOwnerIdValue = "RECEIVE_SUPPLIER_WITH_OWNER_ID";

interface ReceiveSupplierWithOwnerIdAction {
    type: typeof ReceiveSupplierWithOwnerIdValue;
    supplier: Models.SharedMaterialsFullCompany;
}

const AddToLibraryValue = "ADD_TO_LIBRARY";

interface AddToLibraryAction {
    type: typeof AddToLibraryValue;
    libraryInventoryId: string | null;
    state: AddToLibraryState;
}

const SetHasSeenExchangeFirstRunExperienceActionTypeValue = "SET_HAS_SEEN_EXCHANGE_FIRST_RUN_EXPERIENCE";
type SetHasSeenExchangeFirstRunExperienceActionType = typeof SetHasSeenExchangeFirstRunExperienceActionTypeValue;

interface SetHasSeenExchangeFirstRunExperienceAction {
    type: SetHasSeenExchangeFirstRunExperienceActionType;
    hasSeenExchangeFirstRunExperience: boolean;
}

const SetSearchModeValue = "SET_SEARCH_MODE";

interface SetSearchModeAction {
    type: typeof SetSearchModeValue;
    searchMode: BrowserStateModels.BrowserSearchMode;
    newSortContext: BrowserStateModels.SortContext | null; // null means leave it the same as it already is; non-null, then set it to this value
}

const ClearDetailStateValue = "CLEAR_DETAIL_STATE";

interface ClearDetailStateAction {
    type: typeof ClearDetailStateValue;
}

const SetSharedMaterialDetailStatusValue = "SET_SHARED_MATERIAL_DETAIL_STATUS";

interface SetSharedMaterialDetailStatusAction {
    type: typeof SetSharedMaterialDetailStatusValue;
    status: SharedMaterialDetailStatus;
}

// ===============================================================================

//
// Implementation
//

// ===============================================================================

function getDefaultBrowserState(): BrowserState {
    return {
        sortContext: {
            sortType: BrowserStateModels.SortTypes.Featured,
            sortDirection: BrowserStateModels.SortDirectionType.Ascending
        },
        filterMenus: [],
        activeFilters: [],
        filteredSharedMaterialsCoreData: [],
        currentlySelectedSharedMaterialIds: {},
        browserRowModels: [],
        searchMode: BrowserStateModels.BrowserSearchMode.Materials,
    };
}

// ===============================================================================

function shouldIncludeSupplierRow(sortContext: BrowserStateModels.SortContext, activeFilters: BrowserStateModels.ActiveFilter[]) {
    const supplierFilters = activeFilters.filter(f => f.filterId === FilterId.SupplierId);

    return ((supplierFilters.length !== 1) && (sortContext.sortType === BrowserStateModels.SortTypes.SupplierName));
}

// ===============================================================================


function createMapFromSupplierList(suppliers: Models.SharedMaterialsCatalogCompany[]) {
    const returnMap: { [supplierId: string]: Models.SharedMaterialsCatalogCompany } = {};

    for (let supplier of suppliers) {
        returnMap[supplier.companyId] = supplier;
    }

    return returnMap;
}


// ===============================================================================

function buildComplianceFilter(title: string, filterParameter: string): FilterMenu {
    return {
        filterId: title,
        title: title,
        values: [
            BrowserStateModels.RegulationFiterType.Compliant, BrowserStateModels.RegulationFiterType.PossiblyCompliant
        ],
        expanded: true,
        filterParameter: filterParameter
    };
}

// ===============================================================================

function getFilterMenusForSharedMaterials(sharedMaterials: Models.SharedMaterialCoreData[], dynRegs: { [regulationId: string]: DynamicRegulation }, localizer: IStringLocalizer, categories: string[]): BrowserStateModels.FilterMenu[] {
    let menuCategory: FilterMenu[] = [
        {
            filterId: FilterId.Category,
            title: localizer.localizeString("Category"),
            values: categories || [],
            expanded: true,
            filterParameter: null
        }
    ];

    let menuHasSDS: FilterMenu[] = [
        {
            filterId: FilterId.Sds,
            title: localizer.localizeString("Has SDS"),
            values: [
                localizer.localizeString("Yes"), localizer.localizeString("No"),
            ],
            expanded: true,
            filterParameter: null
        }
    ];

    let dynRegComplFilter: FilterMenu[] = [];

    const distinctRegIds = Object.keys(dynRegs);

    for (let distinctRegId of distinctRegIds) {
        dynRegComplFilter.push(buildComplianceFilter(dynRegs[distinctRegId]?.config.name ?? "Unknown Regulation", distinctRegId));
    }

    return menuCategory.concat(dynRegComplFilter, menuHasSDS);
}

// ===============================================================================

function getCompanyName(companyId: string, companies: { [teamOwnerId: string]: Models.SharedMaterialsCatalogCompany }) {
    const company = companies[companyId] ?? null;
    const nameToUse = company?.name ?? "";

    return nameToUse.length === 0 ? "Unknown" : nameToUse;
}

// ===============================================================================

function applyActiveFilters(
    suppliersTable: { [id: string]: Models.SharedMaterialsCatalogCompany },
    allSharedMaterials: Models.SharedMaterialCoreData[],
    activeFilters: Filter[]
): Models.SharedMaterialCoreData[] {

    const filteringManager = new SharedMaterialsFilteringManager();

    const context: SharedMaterialFilterCheckContext = {
        suppliersTable: suppliersTable
    };

    return filteringManager.getFilteredArray(allSharedMaterials, context, activeFilters);
}

// ===============================================================================

function sortSharedMaterials(suppliers: { [teamOwnerId: string]: Models.SharedMaterialsCatalogCompany }, sharedMaterials: Models.SharedMaterialCoreData[], sortContext: BrowserStateModels.SortContext) {
    const getStringValue = (data: Models.SharedMaterialCoreData, sortType: BrowserStateModels.SortTypes) => {
        switch (sortType) {
            case BrowserStateModels.SortTypes.PassportName:
                return data.materialName;
            case BrowserStateModels.SortTypes.SupplierName:
                return getCompanyName(data.ownerId, suppliers);
            case BrowserStateModels.SortTypes.Category:
                return data.categories.join(", ");
        }

        return "";
    }

    // current priorities:
    // photo is #1
    // company name is #2
    // any number of categories is #3
    const getFeaturedBucketValue = (data: Models.SharedMaterialCoreData) => {
        const hasPhotoId = data.materialPhotoFileId !== null;
        const hasCompanyName = getStringValue(data, BrowserStateModels.SortTypes.SupplierName) !== "";
        const hasSomeCategories = data.categories[0] !== "Uncategorized";

        // bucket 0: has everything
        if (hasPhotoId && hasCompanyName && hasSomeCategories) {
            return 0;
        }
        // bucket 1: has photo and company name, no categories
        if (hasPhotoId && hasCompanyName) {
            return 1;
        }
        // bucket 2: has photo and categories, no company name
        if (hasPhotoId && hasSomeCategories) {
            return 2;
        }
        // bucket 3: has categories and company name, no photo
        if (hasCompanyName && hasSomeCategories) {
            return 3;
        }
        // bucket 4: has photo
        if (hasPhotoId) {
            return 4;
        }
        // bucket 5: has company
        if (hasCompanyName) {
            return 5;
        }
        // bucket 6: has categories
        if (hasSomeCategories) {
            return 6;
        }

        // arbitrary large number for "this has nothing"
        return Number.MAX_SAFE_INTEGER;
    }

    // sort function used for the "featured" sort option
    const featuredSort = (a: Models.SharedMaterialCoreData, b: Models.SharedMaterialCoreData) => {
        const a_bucketValue = getFeaturedBucketValue(a);
        const b_bucketValue = getFeaturedBucketValue(b);

        if (a_bucketValue < b_bucketValue) {
            return -1;
        }
        else if (a_bucketValue > b_bucketValue) {
            return 1;
        }
        else {
            return a.materialName.localeCompare(b.materialName);
        }
    }

    // sort function used for any column option (supplier name, passport name, category)
    const columnSort = (a: Models.SharedMaterialCoreData, b: Models.SharedMaterialCoreData) => {
        const v_a = getStringValue(a, sortContext.sortType);
        const v_b = getStringValue(b, sortContext.sortType);
        let i = 0;

        if (v_a !== null && v_b !== null) {
            i = v_a.localeCompare(v_b);

            if (i === 0) {
                // break tie with ownerId
                i = a.ownerId.localeCompare(b.ownerId);

                // if same supplier, sort by material name
                if (i === 0) {
                    i = a.materialName.localeCompare(b.materialName);
                }
            }
        }

        return sortContext.sortDirection === BrowserStateModels.SortDirectionType.Ascending ? i : -i;
    }

    const newArray = [...sharedMaterials];
    newArray.sort(sortContext.sortType === BrowserStateModels.SortTypes.Featured ? featuredSort : columnSort);
    return newArray;
}

// ===============================================================================

function createAllSharedMaterialsCoreDataMap(allSharedMaterials: Models.SharedMaterialCoreData[]) {
    const map: { [sharedMaterialId: string]: Models.SharedMaterialCoreData } = {};

    for (let material of allSharedMaterials) {
        map[material.sharedMaterialId] = material;
    }

    return map;
}

// ===============================================================================

//
// Action creators
//

type KnownAction =
    RequestSharedMaterialsCoreDataAction |
    ReceiveSharedMaterialsCoreDataAction |
    FilterAction |
    SetSortAction |
    UpdateFilterMenusAction |
    RequestSharedMaterialDetailAction |
    ReceiveSharedMaterialDetailAction |
    UpdateDetailDataSectionsAction |
    UpdateRequestStateAction |
    UpdateSelectedSharedMaterialsAction |
    RequestSupplierWithOwnerIdAction |
    ReceiveSupplierWithOwnerIdAction |
    AddToLibraryAction |
    SetHasSeenExchangeFirstRunExperienceAction |
    SetSearchModeAction |
    ClearDetailStateAction |
    SetSharedMaterialDetailStatusAction |
    UpdateDetailStateAction |
    SurveyStateController.SurveySetSurveyRequest |
    SurveyStateController.SurveySetSurveyMetadata |
    RequestSharedMaterialsCompaniesDataAction | 
    ReceiveSharedMaterialsCompaniesDataAction;

// Service Layer API Client.
const toxnotServiceClient = new ToxnotServiceClient();

function createBrowserRowModels(
    supplierMap: { [id: string]: Models.SharedMaterialsCatalogCompany },
    searchMode: BrowserStateModels.BrowserSearchMode,
    filteredSharedMaterialsCoreData: Models.SharedMaterialCoreData[],
    sortContext: BrowserStateModels.SortContext,
    activeFilters: BrowserStateModels.ActiveFilter[]
) {
    const rowModelsToReturn: SharedMaterialDataModelType[] = [];

    if (searchMode === BrowserStateModels.BrowserSearchMode.Companies) {
        rowModelsToReturn.push(...Object.values(supplierMap));
    }

    if (searchMode !== BrowserStateModels.BrowserSearchMode.Companies) {
        rowModelsToReturn.push(...filteredSharedMaterialsCoreData);
    }

    return rowModelsToReturn;
}

function getComputedDataForBrowserState(
    targetBrowserState: BrowserState,
    supplierMap: { [id: string]: Models.SharedMaterialsCatalogCompany },
    materials: Models.SharedMaterialCoreData[],
    dynRegs: { [regulationId: string]: DynamicRegulation },
    localizer: IStringLocalizer,
    categories: string[],
): SpecificBrowserStateData {
    let filteredSharedMaterialsCoreData = applyActiveFilters(supplierMap, materials, targetBrowserState.activeFilters);

    filteredSharedMaterialsCoreData = sortSharedMaterials(supplierMap, filteredSharedMaterialsCoreData, targetBrowserState.sortContext);

    const browserRowModels = createBrowserRowModels(supplierMap, targetBrowserState.searchMode, materials, targetBrowserState.sortContext, targetBrowserState.activeFilters);

    const filterMenus = getFilterMenusForSharedMaterials(materials, dynRegs, localizer, categories);

    return {
        filteredSharedMaterialsCoreData: filteredSharedMaterialsCoreData,
        filterMenus: filterMenus,
        browserRowModels: browserRowModels
    }
}


async function dispatchGetAllSharedSharedMaterialsAsync(state: ApplicationState, localizer: IStringLocalizer, 
    dispatch: (action: KnownAction) => void, 
        accessCode: string | null = null, 
        pageNumber: number = 0, 
        rowPerPage: number = rowPerPageValue, 
        searchText: string = "", 
        selectedFilters: { [filterId: string]: string[]},
        sortType: number = BrowserStateModels.SortTypes.PassportName, 
        sortDirection: SortDirectionType = SortDirectionType.Ascending,
        companyId: string | null = null,
        sharedMaterialId: string | null = null,
    ) {
    if (!state || !state.appServices || !state.sharedMaterialsState) {
        return;
    }

    let filterCategories = "";

    if (selectedFilters?.Category?.length > 0) {
        filterCategories = selectedFilters?.Category.join(",");
    }

    // Prepare additional filters
    const joinOrNull = (arr: string[] | undefined): string | null => 
        arr && arr.length > 0 ? arr.join(",") : null;
    
    const additionalFilters = {
        score_cal65: joinOrNull(selectedFilters['California Proposition 65']),
        score_cm: joinOrNull(selectedFilters['Conflict Minerals']),
        score_cmrt: joinOrNull(selectedFilters['Conflict Minerals (CMRT)']),
        score_reach: joinOrNull(selectedFilters['EU REACH - Candidate List & SCIP']),
        score_reach_auth: joinOrNull(selectedFilters['REACH Authorisation List']),
        score_rohs: joinOrNull(selectedFilters['EU RoHS']),
        score_reach_auth_v2: joinOrNull(selectedFilters['EU REACH Authorisation and Restriction'])
    };

    // While waiting for response, enter loading state
    dispatch({ type: RequestSharedMaterialsCoreDataValue });

    const isSortAcceding = sortDirection == SortDirectionType.Ascending ? true : false;

    let hasSds = null;
    if (selectedFilters?.Sds && selectedFilters?.Sds.length == 1)
        hasSds = selectedFilters?.Sds[0] == "Yes" ? true : false;

    const request: SharedMaterialsCatalogRequest = {
        accessCode: accessCode || null,
        rowsPerPage: rowPerPage,
        pageIndex: pageNumber,
        companyId: companyId || null,
        searchText: searchText,
        categories: filterCategories,
        sortType: sortType,
        sortAscending: isSortAcceding,
        sharedMaterialId: sharedMaterialId || null,
        scoreCal65: additionalFilters.score_cal65,
        scoreCm: additionalFilters.score_cm,
        scoreCmrt: additionalFilters.score_cmrt,
        scoreReach: additionalFilters.score_reach,
        scoreReachAuth: additionalFilters.score_reach_auth,
        scoreRohs: additionalFilters.score_rohs,
        scoreReachAuthV2: additionalFilters.score_reach_auth_v2,
        hasSds: hasSds 
    };

    // Call API
    const apiResult = await toxnotServiceClient.getAllSharedMaterialsCoreData(request);

    if (!apiResult.success || (apiResult.data === null)) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    const totalResult = apiResult.data.catalog.totalNumberOfMaterials;

    const materials = apiResult.data.catalog.materials;
    const categories = apiResult.data.catalog.categories;

    const supplierMap = {};

    const sharedMaterialsCoreDataMap = createAllSharedMaterialsCoreDataMap(materials);

    const specificHomeBrowserStateData = getComputedDataForBrowserState(state.sharedMaterialsState.homeBrowserState, supplierMap, materials, apiResult.data.dynamicRegulationTable, localizer, categories);

    const specificSupplierBrowserStateData = getComputedDataForBrowserState(state.sharedMaterialsState.supplierBrowserState, supplierMap, materials, apiResult.data.dynamicRegulationTable, localizer, categories);

    dispatch({
        type: ReceiveSharedMaterialsCoreDataValue,
        sharedMaterialsCoreData: materials,
        sharedMaterialsCoreDataMap: sharedMaterialsCoreDataMap,
        allSharedMaterialsSuppliers: {},
        specificHomeBrowserStateData: specificHomeBrowserStateData,
        specificSupplierBrowserStateData: specificSupplierBrowserStateData,
        dynamicRegulationTable: apiResult.data.dynamicRegulationTable,
        totalNumberOfCatalog: totalResult,
        currentPage: pageNumber,
        categories: categories,
    });
}

async function dispatchGetAllSharedMaterialsCompaniesData(state: ApplicationState,
    dispatch: (action: KnownAction) => void,
        pageNumber: number = 0,
        rowPerPage: number = rowPerPageValue,
        searchText: string = "",
        sortDirection: SortDirectionType = SortDirectionType.Ascending
    ) {
    if (!state || !state.appServices || !state.sharedMaterialsState) {
        return;
    }

    dispatch({ type: RequestSharedMaterialsCompaniesDataValue });

    const isSortAcceding = sortDirection == SortDirectionType.Ascending ? true : false;

    const apiResult = await toxnotServiceClient.getAllSharedMaterialsCompaniesData(rowPerPage, pageNumber, searchText, isSortAcceding);

    if (!apiResult.success || apiResult.data === null) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    dispatch({
        type: ReceiveSharedMaterialsCompaniesDataValue,
        sharedMaterialsCoreData: apiResult.data.companies,
        totalNumberOfCatalog: apiResult.data.totalNumberOfSharedMaterialCompanies,
        currentPage: pageNumber
    });
};

async function dispatchGetSharedMaterialDetail(state: ApplicationState, sharedMaterialIdToFetch: string, dispatch: (action: KnownAction) => void, accessCode: string | null = null) {
    if (!state || !state.sharedMaterialsState || !state.appServices) {
        return;
    }

    if (state.sharedMaterialsState.sharedMaterialDetailState.detailBundle?.detailData.sharedMaterialId === sharedMaterialIdToFetch) {
        // the given shared material is already loaded
        return;
    }

    dispatch({ type: RequestSharedMaterialDetailValue });

    const apiResult = await toxnotServiceClient.getSharedMaterialDetail(sharedMaterialIdToFetch, accessCode);

    if (!apiResult.success) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    // Means the user does not have access to the shared material or the shared material no longer exists
    if (apiResult.data === null) {
        // If user is not signed in redirect to sign in page.
        if (state.appServices.userContext === null) {
            AppServices.redirectToSignInPage();
            return;
        }

        dispatch({ type: SetSharedMaterialDetailStatusValue, status: SharedMaterialDetailStatus.NotFound })
        return;
    }

    dispatch({
        type: ReceiveSharedMaterialDetailValue,
        detailBundle: apiResult.data
    });
}

async function dispatchGetSupplierIdsAsync(state: ApplicationState, ownerId: string, dispatch: (action: KnownAction) => void, companyId?: string) {
    if (!state || !state.appServices) {
        return;
    }

    dispatch({ type: RequestSupplierWithOwnerIdValue });

    const apiResult = companyId ? await toxnotServiceClient.getSharedMaterialsCompany(ownerId, companyId) : await toxnotServiceClient.getSharedMaterialsCompanyFirst(ownerId);

    if (!apiResult.success || apiResult.data === null) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    dispatch({ type: ReceiveSupplierWithOwnerIdValue, supplier: apiResult.data })
}


function dispatchFilterAction(
    sharedMaterialsCoreData: SharedMaterialDataModelType[],
    targetBrowserState: BrowserState,
    newActiveFilters: Filter[],
    browserEndpoint: BrowserEndpoint,
    dispatch: (action: KnownAction) => void,
    state: SharedMaterialsState
) {
    let filteredSharedMaterialsCoreData = applyActiveFilters({}, sharedMaterialsCoreData as Models.SharedMaterialCoreData[], newActiveFilters);

    filteredSharedMaterialsCoreData = sortSharedMaterials({}, filteredSharedMaterialsCoreData, targetBrowserState.sortContext);

    const newBrowserRows = createBrowserRowModels({}, targetBrowserState.searchMode, filteredSharedMaterialsCoreData, targetBrowserState.sortContext, newActiveFilters);

    dispatch({
        type: FilterActionValue,
        browserEndpoint: browserEndpoint,
        activeFilters: newActiveFilters,
        filteredSharedMaterialsCoreData: filteredSharedMaterialsCoreData,
        browserRows: newBrowserRows
    });
}

function getBrowserStateForBrowserEndpoint(state: SharedMaterialsState, browserEndpoint: BrowserEndpoint) {
    if (browserEndpoint === BrowserEndpoint.Home) {
        return state.homeBrowserState;
    }
    else if (browserEndpoint === BrowserEndpoint.Supplier) {
        return state.supplierBrowserState;
    }
    else {
        throw new Error(`Unknown browser endpoint: ${browserEndpoint}`);
    }
}

export function toggleCollapsedSection(dataSectionId: string, collapsedSections: string[]) {
    const sectionIdIndex = collapsedSections.indexOf(dataSectionId);
    const containsSectionId = -1 === sectionIdIndex;
    if (containsSectionId) {
        collapsedSections.push(dataSectionId);
    }
    else {
        collapsedSections.splice(sectionIdIndex, 1);
    }
}

export const actionCreators = {
    // All materials are loaded in everytime this is called even if they are already in memory. This is to ensure any updates to a shared material are
    // instantly displayed in the homescreen. Therefore, this function should only be called by the top level shared material component.
    // This avoids constantly loading in material when a user is navgating around. 
    getLocalState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        return getState().sharedMaterialsState;
    },
    getPassportTemplate: (coreData: SharedMaterialCoreData, accessCode: string | null): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const serviceClient = new ToxnotServiceClient();

        const metadataApiResult = await serviceClient.getDataCollectionMetadata([coreData.publicInventoryId]);
        const passportApiResult = await serviceClient.passportTemplateGet(coreData.sharedMaterialId, accessCode);

        if (!metadataApiResult.success || !metadataApiResult.data || !passportApiResult.success || !passportApiResult.data) {
            return dispatch({
                type: UpdateDetailStateValue,
                value: { isSlidingDrawerDataError: true }
            });
        }

        dispatch({
            type: SurveyStateController.SurveySetSurveyMetadata,
            value: metadataApiResult.data.surveyData
        });

        const bomQuestionnareIdToUse = passportApiResult.data.bomQuestionnaireId != null ? (metadataApiResult.data.surveyData.questionnaireTable[passportApiResult.data.bomQuestionnaireId] ? passportApiResult.data.bomQuestionnaireId : null) : null;
        const supplierQuestionnareIdToUse = passportApiResult.data.supplierQuestionnaireId != null ? (metadataApiResult.data.surveyData.questionnaireTable[passportApiResult.data.supplierQuestionnaireId] ? passportApiResult.data.supplierQuestionnaireId : null) : null;
        const attestationQuestionnareIdToUse = passportApiResult.data.attestationQuestionnaireId != null ? (metadataApiResult.data.surveyData.questionnaireTable[passportApiResult.data.attestationQuestionnaireId] ? passportApiResult.data.attestationQuestionnaireId : null) : null;

        // on the fly migration of legacy templates

        if (!passportApiResult.data.bomColumns.some(f => f.columnId === "level")) {
            passportApiResult.data.bomColumns.splice(0, 0, {
                columnId: "level",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["level"]
            });
        }

        if (!passportApiResult.data.bomColumns.some(f => f.columnId === "part-name")) {
            passportApiResult.data.bomColumns.splice(1, 0, {
                columnId: "part-name",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["part-name"]
            });
        }

        if (!passportApiResult.data.bomColumns.some(f => f.columnId === "casrn")) {
            passportApiResult.data.bomColumns.splice(2, 0, {
                columnId: "casrn",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["casrn"]
            });
        }

        if (!passportApiResult.data.bomColumns.some(f => f.columnId === "percent-by-weight")) {
            passportApiResult.data.bomColumns.splice(3, 0, {
                columnId: "percent-by-weight",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["percent-by-weight"]
            });
        }

        dispatch({
            type: SurveyStateController.SurveySetSurveyRequest,
            value: {
                ...passportApiResult.data,
                fields: passportApiResult.data.bomColumns,
                nativeLanguage: passportApiResult.data.nativeLanguage ?? "en",
                ccEmailList: passportApiResult.data.ccEmailList.split(","),
                preFillBomQuestionnaireFromSummaryData: passportApiResult.data.preFillBomQuestionnaireFromSummaryData ?? false,
                preFillSupplierQuestionnaireFromSummaryData: passportApiResult.data.preFillSupplierQuestionnaireFromSummaryData ?? false,
                makeBomDataRequiredOnQuestionnaireAnswer: passportApiResult.data.makeBomDataRequiredOnQuestionnaireAnswer ?? false,
                includeAttestationLetter: passportApiResult.data.includeAttestationLetter ?? false
            }
        });

        const dataRequested = {
            complianceDataAsked: passportApiResult.data.dataRequested.complianceList.length > 0,
            documentsAsked: passportApiResult.data.dataRequested.documentsCertificationList.length > 0,
            customQuestionsAsked: bomQuestionnareIdToUse != null || supplierQuestionnareIdToUse != null || attestationQuestionnareIdToUse != null,
        };

        let createAndEditSurveysState = getState().createAndEditSurveysState;

        if (createAndEditSurveysState !== undefined) {
            dispatch({
                type: SurveyStateController.SurveySetSurveyRequest,
                value: {
                    dataRequested: {
                        ...createAndEditSurveysState.surveyRequest.dataRequested,
                        ...dataRequested,
                    }
                }
            });
        }

        let globalState = getState();

        if (!globalState.sharedMaterialsState?.sharedMaterialDetailState.isSnapshotSurveyStateSaved) {
            createAndEditSurveysState = getState().createAndEditSurveysState;

            dispatch({
                type: UpdateDetailStateValue,
                value: {
                    snapshotSurveyState: createAndEditSurveysState,
                    isSnapshotSurveyStateSaved: true
                }
            });
        }

        dispatch({
            type: UpdateDetailStateValue,
            value: { isSlidingDrawerDataLoaded: true }
        });
    },
    sendRequestInformationSurvey: (coreData: SharedMaterialCoreData, libraryInventoryId: string | null, accessCode: string | null): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const applicationState = getState();
        const surveyEditorstate = applicationState.createAndEditSurveysState;

        if (!surveyEditorstate) {
            return;
        }

        const surveyRequest = surveyEditorstate.surveyRequest;

        const data: SurveyEditorCreateNewSurveyRequestData = {
            ...surveyRequest,
            supplierModels: [],
            shouldSend: true,
            ...surveyRequest.dataRequested,
            questionnaireAnswerThatMakesBomDataRequired: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.questionnaireAnswerThatMakesBomDataRequired ?? null : null,
            documentsCertificationsAskedList: surveyRequest.dataRequested.documentsAsked ? surveyRequest.dataRequested.documentsCertificationList : [],
            complianceDataAskedList: surveyRequest.dataRequested.complianceDataAsked ? surveyRequest.dataRequested.complianceList : [],
            supplierQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.supplierQuestionnaireId : null,
            bomQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.bomQuestionnaireId : null,
            attestationQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.attestationQuestionnaireId : null,
        };

        dispatch({
            type: UpdateDetailStateValue,
            value: { isSendingSurveys: true, isSendingSurveysError: false, isSendingSurveysSuccess: false }
        });

        $("#send-survey-modal").modal("show");

        const serviceClient = new ToxnotServiceClient();

        if (libraryInventoryId === null) {
            dispatch({ type: AddToLibraryValue, libraryInventoryId: null, state: AddToLibraryState.Adding });

            const addToLibraryApiResult = await serviceClient.addSharedMaterialToLibrary(coreData.sharedMaterialId);

            if (!addToLibraryApiResult.data) {
                applicationState.appServices?.displayApiErrorMessage(applicationState.appServices.localizeString("Sorry, there was a problem adding to your library."))
            }

            dispatch({ type: AddToLibraryValue, libraryInventoryId: addToLibraryApiResult.data, state: addToLibraryApiResult.success ? AddToLibraryState.Added : AddToLibraryState.Failed });
        }

        const postApiResult = await serviceClient.passportTemplatePost(coreData.sharedMaterialId, accessCode, data);

        if (postApiResult.result !== ResultType.Ok) {
            dispatch({
                type: UpdateDetailStateValue,
                value: { isSendingSurveysError: true }
            });
        } else {
            dispatch({
                type: UpdateDetailStateValue,
                value: { isSendingSurveysSuccess: true }
            });
        }

        dispatch({
            type: UpdateDetailStateValue,
            value: { isSendingSurveys: false }
        });
    },
    updateDetailStateValue: (newValue: Partial<SharedMaterialDetailState>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({
            type: UpdateDetailStateValue,
            value: newValue
        });
    },
    getAllSharedMaterialsCompaniesData: (
        pageNumber: number = 0,
        rowPerPage: number = rowPerPageValue,
        searchText: string = "",
        sortDirection: SortDirectionType = SortDirectionType.Ascending
    ): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState();
    
        if (!state || !state.sharedMaterialsState || !state.appServices) {
            return;
        }

        dispatchGetAllSharedMaterialsCompaniesData(state, dispatch, pageNumber, rowPerPage, searchText, sortDirection) 
    },
    loadSharedMaterialsIfNeeded: (localizer: IStringLocalizer, 
        urlEncodedAccessCode: string | null = null, 
        pageNumber: number = 0, 
        searchText = "", 
        rowPerPage: number = rowPerPageValue, 
        selectedFilters: { [filterId: string]: string[]}, 
        sortType: BrowserStateModels.SortTypes = BrowserStateModels.SortTypes.PassportName, 
        sortDirection: SortDirectionType = SortDirectionType.Ascending,
        companyId: string | null = null,
        sharedMaterialId: string | null = null,

    ): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        dispatchGetAllSharedSharedMaterialsAsync(appState, localizer, dispatch, urlEncodedAccessCode, pageNumber, rowPerPage, searchText, selectedFilters, sortType, sortDirection, companyId, sharedMaterialId);
    },
    loadExchangeFreState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        const hasSeenExchangeFirstRunExperience = (localStorage.getItem("has-seen-exhange-fre") ?? "false") === "true";

        dispatch({
            type: SetHasSeenExchangeFirstRunExperienceActionTypeValue,
            hasSeenExchangeFirstRunExperience: hasSeenExchangeFirstRunExperience
        });
    },
    setHasSeenExchangeFreState: (hasSeenExchangeFirstRunExperience: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        localStorage.setItem("has-seen-exhange-fre", hasSeenExchangeFirstRunExperience ? "true" : "false");

        dispatch({
            type: SetHasSeenExchangeFirstRunExperienceActionTypeValue,
            hasSeenExchangeFirstRunExperience: hasSeenExchangeFirstRunExperience
        });
    },
    toggleFilterMenu: (browserEndpoint: BrowserEndpoint, filterId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        const sharedMaterialsState = appState.sharedMaterialsState;
        const targetBrowserState = getBrowserStateForBrowserEndpoint(sharedMaterialsState, browserEndpoint);

        const newFilterMenus = targetBrowserState.filterMenus.map(filterMenu => {
            if (filterMenu.filterId !== filterId) {
                return filterMenu;
            }
            else {
                return {
                    ...filterMenu,
                    expanded: !filterMenu.expanded
                }
            }
        });

        dispatch({
            type: UpdateFilterMenusValue,
            filterMenus: newFilterMenus,
            browserEndpoint
        });
    },
    activateFilter: (browserEndpoint: BrowserEndpoint, filterId: FilterId, value: string, filterParameter: string | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        const sharedMaterialsState = appState.sharedMaterialsState;

        const targetBrowserState = getBrowserStateForBrowserEndpoint(sharedMaterialsState, browserEndpoint);

        if (targetBrowserState === null) {
            return;
        }

        const newActiveFilters = targetBrowserState.activeFilters.some(f => (f.filterId === filterId) && (f.value === value))
            ? targetBrowserState.activeFilters
            : targetBrowserState.activeFilters.concat({
                filterId: filterId,
                value: value,
                filterParameter: filterParameter
            });

        dispatchFilterAction(sharedMaterialsState.sharedMaterialsCoreData, targetBrowserState, newActiveFilters, browserEndpoint, dispatch, sharedMaterialsState);
    },
    deactivateFilter: (browserEndpoint: BrowserEndpoint, filterIdToRemove: string, filterValueToRemove: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        const sharedMaterialsState = appState.sharedMaterialsState;

        const targetBrowserState = getBrowserStateForBrowserEndpoint(sharedMaterialsState, browserEndpoint);

        if (targetBrowserState === null) {
            return;
        }

        const newActiveFilters = targetBrowserState.activeFilters.filter(f => (f.filterId !== filterIdToRemove) || (f.value !== filterValueToRemove));

        dispatchFilterAction(sharedMaterialsState.sharedMaterialsCoreData, targetBrowserState, newActiveFilters, browserEndpoint, dispatch, sharedMaterialsState);
    },
    loadSharedMaterialDetail: (sharedMaterialIdToFetch: string, localizer: IStringLocalizer, accessCode: string | null = null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        if (appState.sharedMaterialsState.sharedMaterialDetailState === null) {
            return;
        }
        
        dispatchGetSharedMaterialDetail(appState, sharedMaterialIdToFetch, dispatch, accessCode);
    },
    clearSharedMaterialDetailState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        if (appState.sharedMaterialsState.sharedMaterialDetailState === null) {
            return;
        }

        dispatch({ type: ClearDetailStateValue });
    },
    toggleDetailDataSection: (dataSectionId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        const sharedMaterialDetail = appState.sharedMaterialsState.sharedMaterialDetailState;

        if (sharedMaterialDetail === null) {
            return;
        }

        const collapsedSections = sharedMaterialDetail.collapsedSections;

        toggleCollapsedSection(dataSectionId, collapsedSections);

        dispatch({ type: UpdateDetailDataSectionsValue, collapsedSections: collapsedSections });
    },
    initializeRequest: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        dispatch({ type: UpdateRequestStateValue, requestState: BrowserStateModels.RequestState.Default });
    },
    requestAccessToSharedMaterials: (sharedMaterialIds: string[]): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        if (sharedMaterialIds.length === 0) {
            return;
        }

        const sharedMaterialsBeingRequested = Object.values(appState.sharedMaterialsState.sharedMaterialsCoreDataMap).filter(s => sharedMaterialIds.includes(s.sharedMaterialId));

        // The shared materials being requested should all have the same owner and company id
        if ((sharedMaterialsBeingRequested.map(s => s.ownerId).distinct().length > 1) ||
            (sharedMaterialsBeingRequested.map(s => s.companyId).distinct().length > 1)) {
            throw new Error("Passports being requested did not have same owner or company ids");
        }

        dispatch({ type: UpdateRequestStateValue, requestState: BrowserStateModels.RequestState.Sending });

        const apiResult = await toxnotServiceClient.requestAccessPermission({
            sharedMaterialsOwnerId: sharedMaterialsBeingRequested[0].ownerId,
            sharedMaterialIds: sharedMaterialIds,
        })

        dispatch({ type: UpdateRequestStateValue, requestState: apiResult.success ? BrowserStateModels.RequestState.Sent : BrowserStateModels.RequestState.FailedToSend });
    },
    contactCompany: (companyId: string, message: string, sharedMaterialId?: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState || !appState.appServices) {
            return;
        }

        dispatch({ type: UpdateRequestStateValue, requestState: BrowserStateModels.RequestState.Sending });

        let ownerId = '';
        if (appState.sharedMaterialsState.sharedMaterialsCoreData.length > 0) {
            ownerId = appState.sharedMaterialsState.sharedMaterialsCoreData.filter((data) => data.companyId == companyId)[0].ownerId 
        } else if(appState.appServices.userContext) {
            ownerId= appState.appServices.userContext.teamOwnerId
        }
        
        const apiResult = await toxnotServiceClient.contactCompany({
            ownerId: ownerId,
            companyId: companyId,
            message: message,
            sharedMaterialId: sharedMaterialId
        });

        dispatch({ type: UpdateRequestStateValue, requestState: apiResult.success ? BrowserStateModels.RequestState.Sent : BrowserStateModels.RequestState.FailedToSend });
    },
    shareMaterialByEmail: (emailAddresses: string[], message: string, sharedMaterialId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        toxnotServiceClient.shareMaterialByEmail({ emailAddresses: emailAddresses, message: message, sharedMaterialId: sharedMaterialId })
            .then(apiResult => {
                dispatch({ type: UpdateRequestStateValue, requestState: apiResult.success ? BrowserStateModels.RequestState.Sent : BrowserStateModels.RequestState.FailedToSend });
            });

        dispatch({ type: UpdateRequestStateValue, requestState: BrowserStateModels.RequestState.Sending });
    },
    selectSharedMaterial: (selectedMaterialIds: {
        [id: string]: string;
    }): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        dispatch({ type: UpdateSelectedSharedMaterialsValue, selectedMaterialIds: selectedMaterialIds });
    },
    getSupplierFromIds: (ownerId: string, companyId?: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        await dispatchGetSupplierIdsAsync(appState, ownerId, dispatch, companyId);
    },
    addToMyLibrary: (sharedMaterialId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!(appState && appState.sharedMaterialsState && appState.sharedMaterialsState.sharedMaterialDetailState && appState.appServices)) {
            return;
        }

        toxnotServiceClient.addSharedMaterialToLibrary(sharedMaterialId)
            .then(result => {
                if (!result.data) {
                    appState.appServices?.displayApiErrorMessage(appState.appServices.localizeString("Sorry, there was a problem adding to your library."))
                }

                dispatch({ type: AddToLibraryValue, libraryInventoryId: result.data, state: result.success ? AddToLibraryState.Added : AddToLibraryState.Failed });
            });

        dispatch({ type: AddToLibraryValue, libraryInventoryId: null, state: AddToLibraryState.Adding });
    },
    goToSourceMaterial: (publicInventoryId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!(appState && appState.sharedMaterialsState && appState.sharedMaterialsState.sharedMaterialDetailState && appState.appServices)) {
            return;
        }

        toxnotServiceClient.getSourceInventoryIdForPublicInventoryId(publicInventoryId)
            .then(result => {
                if (!result.data) {
                    appState.appServices?.displayApiErrorMessage(appState.appServices.localizeString("Sorry, there was a problem finding the source Material."))
                }
                else {
                    location.href = "/Data/Inventory?inventoryId=" + encodeURIComponent(result.data);
                }
            });
    },
    setSearchMode: (searchMode: BrowserStateModels.BrowserSearchMode): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsState) {
            return;
        }

        let newSortContext: BrowserStateModels.SortContext | null = null;

        if ((searchMode === BrowserStateModels.BrowserSearchMode.Companies) && (appState.sharedMaterialsState.homeBrowserState.sortContext.sortType !== BrowserStateModels.SortTypes.SupplierName)) {
            newSortContext = {
                sortType: BrowserStateModels.SortTypes.SupplierName,
                sortDirection: BrowserStateModels.SortDirectionType.Ascending
            };
        }

        dispatch({
            type: SetSearchModeValue,
            searchMode: searchMode,
            newSortContext
        });
    }
};

// ===============================================================================

//
// Reducer
//

const defaultCurrentlySelectedSupplier: CurrentlySelectedSupplierState = {
    isLoading: true,
    supplier: null
};

const defaultSharedMaterialDetailState: SharedMaterialDetailState = {
    status: SharedMaterialDetailStatus.Loading,
    detailBundle: null,
    collapsedSections: [],
    addingState: AddToLibraryState.Default,
    isSlidingDrawerOpen: false,
    isSlidingDrawerDataLoaded: false,
    isSlidingDrawerDataError: false,
    isSendingSurveys: false,
    isSendingSurveysError: false,
    isSendingSurveysSuccess: false,
    isSnapshotSurveyStateSaved: false,
    snapshotSurveyState: SurveyStateController.getInitialState("en", true)
}

const initialState: SharedMaterialsState = {
    isLoadingCatalog: true,
    sharedMaterialsCoreData: [],
    sharedMaterialsCoreDataMap: {},
    totalNumberOfCatalog: 0,
    currentPage: 0,
    homeBrowserState: { ...getDefaultBrowserState() },
    supplierBrowserState: {
        ...getDefaultBrowserState(),
        sortContext: {
            sortType: BrowserStateModels.SortTypes.PassportName,
            sortDirection: BrowserStateModels.SortDirectionType.Ascending
        }
    },
    sharedMaterialDetailState: {
        ...defaultSharedMaterialDetailState
    },
    requestState: null,
    currentlySelectedSupplierState: defaultCurrentlySelectedSupplier,
    hasSeenExchangeFirstRunExperience: false,
    dynamicRegulationTable: {}
};

function getCopyOfTargetBrowserState(state: SharedMaterialsState, browserEndpoint: BrowserEndpoint) {
    if (browserEndpoint === BrowserEndpoint.Home) {
        return { ...state.homeBrowserState };
    }
    else if ((browserEndpoint === BrowserEndpoint.Supplier) && (state.supplierBrowserState !== null)) {
        return { ...state.supplierBrowserState };
    }
    else {
        throw new Error(`invalid browser endpoint - ${browserEndpoint}`);
    }
}

export const reducer: Reducer<SharedMaterialsState> = (state: SharedMaterialsState | undefined, incomingAction: Action): SharedMaterialsState => {
    if (state === undefined) {
        return initialState;
    }

    let targetBrowserState: BrowserState;

    switch (incomingAction.type) {
        case UpdateDetailStateValue:
            const detailStateAction = incomingAction as AnyAction;

            return {
                ...state,
                sharedMaterialDetailState: {
                    ...state.sharedMaterialDetailState,
                    ...detailStateAction.value
                }
            };

        case RequestSharedMaterialsCoreDataValue:
            return {
                ...state,
                isLoadingCatalog: true,
                sharedMaterialsCoreData: [],
            };

        case ReceiveSharedMaterialsCoreDataValue:
            const receiveMaterialsCoreDataAction = incomingAction as ReceiveSharedMaterialsCoreDataAction;
            return {
                ...state,
                isLoadingCatalog: false,
                sharedMaterialsCoreData: receiveMaterialsCoreDataAction.sharedMaterialsCoreData,
                sharedMaterialsCoreDataMap: receiveMaterialsCoreDataAction.sharedMaterialsCoreDataMap,
                totalNumberOfCatalog: receiveMaterialsCoreDataAction.totalNumberOfCatalog,
                currentPage: receiveMaterialsCoreDataAction.currentPage,
                homeBrowserState: {
                    sortContext: state.homeBrowserState.sortContext,
                    activeFilters: state.homeBrowserState.activeFilters,
                    currentlySelectedSharedMaterialIds: {},
                    searchMode: state.homeBrowserState.searchMode,
                    ...receiveMaterialsCoreDataAction.specificHomeBrowserStateData
                },
                supplierBrowserState: {
                    sortContext: state.supplierBrowserState.sortContext,
                    activeFilters: state.supplierBrowserState.activeFilters,
                    currentlySelectedSharedMaterialIds: {},
                    searchMode: state.supplierBrowserState.searchMode,
                    ...receiveMaterialsCoreDataAction.specificSupplierBrowserStateData
                },
                dynamicRegulationTable: receiveMaterialsCoreDataAction.dynamicRegulationTable
            };
        case RequestSharedMaterialsCompaniesDataValue:
            return {
                ...state,
                isLoadingCatalog: true
            };
        
        case ReceiveSharedMaterialsCompaniesDataValue:
            const receiveCompaniesDataAction = incomingAction as ReceiveSharedMaterialsCompaniesDataAction;
            return {
                ...state,
                isLoadingCatalog: false,
                sharedMaterialsCoreData: receiveCompaniesDataAction.sharedMaterialsCoreData,
                totalNumberOfCatalog: receiveCompaniesDataAction.totalNumberOfCatalog,
                currentPage: receiveCompaniesDataAction.currentPage,
            };


        case SetSortValue:
            const setSortAction = incomingAction as SetSortAction;

            targetBrowserState = getCopyOfTargetBrowserState(state, setSortAction.browserEndpoint);

            targetBrowserState.sortContext = setSortAction.sortContext;
            targetBrowserState.filteredSharedMaterialsCoreData = setSortAction.filteredSharedMaterialsCoreData;
            targetBrowserState.browserRowModels = setSortAction.browserRows;

            return {
                ...state,
                homeBrowserState: setSortAction.browserEndpoint === BrowserEndpoint.Home ? targetBrowserState : state.homeBrowserState,
                supplierBrowserState: setSortAction.browserEndpoint === BrowserEndpoint.Supplier ? targetBrowserState : state.supplierBrowserState,
            };

        case FilterActionValue:
            const filterAction = incomingAction as FilterAction;

            targetBrowserState = getCopyOfTargetBrowserState(state, filterAction.browserEndpoint);

            targetBrowserState.activeFilters = filterAction.activeFilters;
            targetBrowserState.filteredSharedMaterialsCoreData = filterAction.filteredSharedMaterialsCoreData;
            targetBrowserState.browserRowModels = filterAction.browserRows;

            return {
                ...state,
                homeBrowserState: filterAction.browserEndpoint === BrowserEndpoint.Home ? targetBrowserState : state.homeBrowserState,
                supplierBrowserState: filterAction.browserEndpoint === BrowserEndpoint.Supplier ? targetBrowserState : state.supplierBrowserState,
            };

        case UpdateFilterMenusValue:
            const updateFilterMenusAction = incomingAction as UpdateFilterMenusAction;

            targetBrowserState = getCopyOfTargetBrowserState(state, updateFilterMenusAction.browserEndpoint);

            targetBrowserState.filterMenus = updateFilterMenusAction.filterMenus;

            return {
                ...state,
                homeBrowserState: updateFilterMenusAction.browserEndpoint === BrowserEndpoint.Home ? targetBrowserState : state.homeBrowserState,
                supplierBrowserState: updateFilterMenusAction.browserEndpoint === BrowserEndpoint.Supplier ? targetBrowserState : state.supplierBrowserState,
            };

        case RequestSharedMaterialDetailValue:
            return {
                ...state,
                sharedMaterialDetailState: {
                    ...state.sharedMaterialDetailState,
                    status: SharedMaterialDetailStatus.Loading,
                    collapsedSections: state.sharedMaterialDetailState.collapsedSections,
                    detailBundle: null
                },
            };

        case ReceiveSharedMaterialDetailValue:
            const receiveSharedMaterialDetailAction = incomingAction as ReceiveSharedMaterialDetailAction;

            return {
                ...state,
                sharedMaterialDetailState: {
                    ...state.sharedMaterialDetailState,
                    status: SharedMaterialDetailStatus.Found,
                    collapsedSections: state.sharedMaterialDetailState.collapsedSections,
                    detailBundle: receiveSharedMaterialDetailAction.detailBundle,
                },
            };

        case UpdateDetailDataSectionsValue:
            if (state.sharedMaterialDetailState === null) {
                throw new Error("Invalid operation - shared material detail is null");
            }

            return {
                ...state,
                sharedMaterialDetailState: {
                    ...state.sharedMaterialDetailState,
                    status: state.sharedMaterialDetailState.status,
                    collapsedSections: (incomingAction as UpdateDetailDataSectionsAction).collapsedSections,
                    detailBundle: state.sharedMaterialDetailState.detailBundle
                },
            };

        case UpdateRequestStateValue:
            const newRequestState = (incomingAction as UpdateRequestStateAction).requestState;

            return {
                ...state,
                requestState: newRequestState === null
                    ? null
                    : {
                        state: newRequestState
                    },
            };

        case UpdateSelectedSharedMaterialsValue:
            const selectSharedMaterialAction = (incomingAction as UpdateSelectedSharedMaterialsAction);

            return {
                ...state,
                supplierBrowserState: {
                    ...state.supplierBrowserState,
                    currentlySelectedSharedMaterialIds: selectSharedMaterialAction.selectedMaterialIds
                },
            };

        case RequestSupplierWithOwnerIdValue:
            return {
                ...state,
                currentlySelectedSupplierState: {
                    ...defaultCurrentlySelectedSupplier,
                }
            };
        case ReceiveSupplierWithOwnerIdValue:
            const receiveSupplierAction = incomingAction as ReceiveSupplierWithOwnerIdAction;
            return {
                ...state,
                currentlySelectedSupplierState: {
                    isLoading: false,
                    supplier: receiveSupplierAction.supplier
                }
            };
        case AddToLibraryValue:
            const addToLibraryAction = incomingAction as AddToLibraryAction;
            return {
                ...state,
                sharedMaterialDetailState: {
                    ...state.sharedMaterialDetailState,
                    detailBundle: {
                        ...state.sharedMaterialDetailState.detailBundle!,
                        libraryInventoryId: addToLibraryAction.libraryInventoryId
                    },
                    addingState: addToLibraryAction.state,
                }
            };
        case SetHasSeenExchangeFirstRunExperienceActionTypeValue:
            return {
                ...state,
                hasSeenExchangeFirstRunExperience: (incomingAction as SetHasSeenExchangeFirstRunExperienceAction).hasSeenExchangeFirstRunExperience
            };
        case SetSearchModeValue:
            const setSearchModeAction = incomingAction as SetSearchModeAction;

            return {
                ...state,
                homeBrowserState: {
                    ...state.homeBrowserState,
                    activeFilters: state.homeBrowserState.activeFilters,
                    sortContext: setSearchModeAction.newSortContext ?? { ...state.homeBrowserState.sortContext },
                    searchMode: setSearchModeAction.searchMode,
                }
            };
        case ClearDetailStateValue:
            return {
                ...state,
                sharedMaterialDetailState: {
                    ...defaultSharedMaterialDetailState
                }
            };
        case SetSharedMaterialDetailStatusValue:
            const setSharedMaterialDetailStatusAction = incomingAction as SetSharedMaterialDetailStatusAction;
            return {
                ...state,
                sharedMaterialDetailState: {
                    ...defaultSharedMaterialDetailState,
                    status: setSharedMaterialDetailStatusAction.status
                }
            };
    }

    return state;
}
